// Real HTTP backend pointing at the @osmix/api-gateway service. The dispatcher
// in ./client.ts uses this whenever `BUN_PUBLIC_API_GATEWAY_URL` is set (the
// running stack always sets it). Responses are unwrapped from the gateway
// envelope and parsed against the zod schemas in ./types.ts at the boundary.
import { ApiClientError, createApiClient, type ApiClient } from "@osmix/api-client";
import type { z } from "zod";
import type { ShopInvite, ShopInviteBindResult } from "@osmix/contracts/seller-clients";
import { CartPromoFeedSchema } from "@osmix/contracts/promo-partners";
import { ShopCustomOrderListSchema, ShopCustomOrderSchema } from "@osmix/contracts/shop-custom-order";
import {
	AddressSuggestion,
	BuyerAddress,
	BuyerCompanySuggestion,
	BuyerRecipient,
	SavedPickupPoint,
	BuyerAuthStartResponse,
	BuyerAuthVerifyResponse,
	OAuthExchangeResult,
	AccountDeletionCheck,
	AccountDeletionResult,
	OAuthIdentity,
	OAuthProviderInfo,
	BuyerMe,
	BuyerMessage,
	BuyerPaymentMethod,
	BuyerBalance,
	LedgerEntry,
	BalanceTopup,
	TopupConfirmResult,
	BillingProfile,
	Invoice,
	BuyerSession,
	Cart,
	ArchivedCategory,
	Category,
	ChatBulkResult,
	ChatConversation,
	ChatMessage,
	ChatUpload,
	CitySuggestion,
	Country,
	Currency,
	GeoReverse,
	DeliveryConfig,
	DeliveryQuote,
	FeedBlockWire,
	FeedCollection,
	ProductCollectionParticipation,
	Order,
	OrderShipment,
	PaginatedLikeIds,
	PickupPoint,
	SelfDeliveryShopOption,
	SellerPickupShopOption,
	ProductDeliveryOptions,
	LikeState,
	DeviceAttachResult,
	LikeTargetResult,
	HiddenIdeaState,
	HiddenTargetResult,
	NotificationItemSchema,
	NotificationPageSchema,
	NotificationUnreadCountSchema,
	NotificationPreferencesSchema,
	NotificationTopicCountsSchema,
	TelegramLinkStartSchema,
	TelegramLinkStatusSchema,
	NotificationTopicSettingsSchema,
	PhoneChangeNew,
	PhoneChangeStart,
	PhoneChangeVerify,
	SbpBank,
	FaqListSchema,
	SupportTicketSchema,
	SupportTicketDetailSchema,
	MetrikaConfigSchema,
	SeoPageSchema,
	WebPushConfigSchema,
	Product,
	ProductManufacturingRequest,
	ProductMessage,
	ProductFacets,
	ProductPage,
	PersonalFeedPage,
	ProductReviewListSchema,
	ProductQuestionSchema,
	ProductQuestionListSchema,
	PaymentUrl,
	SavedCardPayResult,
	PublicPaymentConfig,
	Shop,
	Storefront,
	BuyerRequestListSchema,
	BuyerRequestDetailSchema,
	BuyerRequestAttachmentSchema,
	RequestOfferSchema,
	type AddCartItemRequest,
	type Api,
	type BuyerAddressInput,
	type BuyerPaymentMethodInput,
	type BuyerPaymentMethodPatch,
	type BillingProfileInput,
	type CreateOrderRequest,
	type DeliveryQuoteRequest,
	type ProductQuery,
	type ProductReviewsQuery,
} from "./types";
import { getBuyerAuthToken } from "./token";
import { accessHeaders, rememberCartToken, rememberOrderTokens } from "./access-tokens";
import { createCabinetHttp } from "./cabinet";
import { createPromosHttp } from "./promos";
import { createLooksHttp } from "./looks";
import { createUgcHttp } from "./ugc";

type Envelope<T> =
	| { success: true; data: T }
	| { success: false; error: { code: string; message: string; details?: unknown } };

const clients = new Map<string, ApiClient>();

const clientFor = (baseUrl: string): ApiClient => {
	const existing = clients.get(baseUrl);
	if (existing) return existing;
	const client = createApiClient({ baseUrl, getBearerToken: getBuyerAuthToken });
	clients.set(baseUrl, client);
	return client;
};

const request = async <T>(
	baseUrl: string,
	path: string,
	// `init` пробрасывает поля fetch, которых нет в типизированных опциях
	// api-client. Нужен ровно одному месту — телеметрии витрины с
	// `keepalive: true`, чтобы пачка событий доехала при закрытии вкладки.
	opts: {
		method?: string;
		body?: unknown;
		auth?: boolean;
		headers?: Record<string, string>;
		init?: Omit<RequestInit, "body" | "headers" | "method" | "signal">;
	} = {},
): Promise<T> => {
	try {
		const payload = await clientFor(baseUrl).request<Envelope<T> | undefined>(path, opts);
		if (payload == null) return undefined as T;
		if (payload.success) return payload.data;
		const err = payload && payload.success === false ? payload.error : null;
		throw new Error(err?.message ?? "Request failed");
	} catch (error) {
		if (error instanceof ApiClientError) {
			const payload = error.body as Envelope<T> | null;
			const err = payload && payload.success === false ? payload.error : null;
			throw new Error(err?.message ?? `Request failed: ${error.status}`);
		}
		throw error;
	}
};

const buildSearch = (query: ProductQuery): string => {
	const p = new URLSearchParams();
	if (query.search) p.set("search", query.search);
	if (query.shopId) p.set("shopId", query.shopId);
	if (query.categoryId) p.set("categoryId", query.categoryId);
	for (const c of query.categoryIds ?? []) p.append("categoryId", c);
	for (const b of query.brands ?? []) p.append("brand", b);
	for (const c of query.colors ?? []) p.append("color", c);
	for (const s of query.sizes ?? []) p.append("size", s);
	if (query.priceMin != null) p.set("priceMin", String(query.priceMin));
	if (query.priceMax != null) p.set("priceMax", String(query.priceMax));
	if (query.inStock) p.set("inStock", "true");
	for (const type of query.productTypes ?? []) p.append("productType", type);
	if (query.sale) p.set("sale", "true");
	if (query.city) p.set("city", query.city);
	if (query.collectionId) p.set("collectionId", query.collectionId);
	if (query.sort) p.set("sort", query.sort);
	p.set("page", String(query.page ?? 1));
	p.set("pageSize", String(query.pageSize ?? 24));
	return p.toString();
};

// Списки кабинета — это снимки, накопленные за всё время: в одном заказе может
// не хватать поля, которое схема уже требует. `Schema.array().parse()` в таком
// случае падает на всём массиве и уносит с собой весь раздел, поэтому элементы
// валидируем поштучно: валидные отдаём, невалидный пропускаем с предупреждением.
const parseItems = <T>(schema: z.ZodType<T>, raw: unknown, label: string): T[] => {
	if (!Array.isArray(raw)) {
		console.warn(`[api] ${label}: expected an array, got ${typeof raw}`);
		return [];
	}
	const items: T[] = [];
	for (const [index, entry] of raw.entries()) {
		const parsed = schema.safeParse(entry);
		if (parsed.success) items.push(parsed.data);
		else console.warn(`[api] ${label}: skipped malformed item #${index}`, parsed.error.issues);
	}
	return items;
};

const enc = encodeURIComponent;

const pageQuery = (input: { page?: number; pageSize?: number } = {}) => {
	const p = new URLSearchParams();
	p.set("page", String(input.page ?? 1));
	p.set("pageSize", String(input.pageSize ?? 20));
	return p.toString();
};

export const createHttp = (baseUrl: string): Api => ({
	cabinet: createCabinetHttp((path, opts) => request(baseUrl, path, opts)),
	promos: createPromosHttp((path, opts) => request(baseUrl, path, opts)),
	looks: createLooksHttp((path, opts) => request(baseUrl, path, opts)),
	ugc: createUgcHttp((path, opts) => request(baseUrl, path, opts)),
	products: {
		list: async (query) =>
			ProductPage.parse(await request(baseUrl, `/catalog/search?${buildSearch(query)}`)),
		get: async (id) => Product.parse(await request(baseUrl, `/catalog/products/${enc(id)}`)),
		byIds: async (ids) =>
			Product.array().parse(
				await request(baseUrl, "/catalog/products/by-ids", { method: "POST", body: { ids } }),
			),
		facets: async (query) => {
			const p = new URLSearchParams();
			if (query.shopId) p.set("shopId", query.shopId);
			if (query.search) p.set("search", query.search);
			// categoryId сужает оси (размер/бренд/цвет/цена) под выбранную категорию:
			// у обуви — размеры обуви, а не XS/S/M/L. Гейтвей учитывает подкатегории.
			if (query.categoryId) p.set("categoryId", query.categoryId);
			return ProductFacets.parse(await request(baseUrl, `/catalog/facets?${p.toString()}`));
		},
		reviews: async (id, query: ProductReviewsQuery = {}) => {
			const p = new URLSearchParams();
			if (query.sort) p.set("sort", query.sort);
			if (query.rating != null) p.set("rating", String(query.rating));
			if (query.withPhotos) p.set("withPhotos", "true");
			if (query.variant) p.set("variant", query.variant);
			const qs = p.toString();
			return ProductReviewListSchema.parse(
				await request(baseUrl, `/products/${enc(id)}/reviews${qs ? `?${qs}` : ""}`),
			);
		},
		questions: async (id) =>
			ProductQuestionListSchema.parse(await request(baseUrl, `/products/${enc(id)}/questions`)),
	},
	categories: {
		list: async () => Category.array().parse(await request(baseUrl, "/catalog/categories")),
		archived: async () =>
			ArchivedCategory.array().parse(await request(baseUrl, "/catalog/categories/archived")),
	},
	shops: {
		list: async () => Shop.array().parse(await request(baseUrl, "/shops")),
		get: async (slug) => Shop.parse(await request(baseUrl, `/shops/${enc(slug)}`)),
		storefront: async (slug) =>
			Storefront.parse(await request(baseUrl, `/shops/${enc(slug)}/storefront`)),
		createCustomOrder: async (slug, input) =>
			ShopCustomOrderSchema.parse(
				await request(baseUrl, `/shops/${enc(slug)}/custom-orders`, { method: "POST", body: input }),
			),
	},
	cart: (() => {
		// Корзина — с токеном доступа (X-Cart-Token), ответ приносит его же.
		const cartCall = async (id: string, path: string, opts: { method?: string; body?: unknown } = {}) => {
			const raw = await request(baseUrl, path, { ...opts, headers: accessHeaders("cart", id) });
			rememberCartToken(raw);
			return Cart.parse(raw);
		};
		return {
			create: async () => {
				const raw = await request(baseUrl, "/cart", { method: "POST" });
				rememberCartToken(raw);
				return Cart.parse(raw);
			},
			get: async (id: string) => cartCall(id, `/cart/${enc(id)}`),
			addItem: async (id: string, req: AddCartItemRequest) =>
				cartCall(id, `/cart/${enc(id)}/items`, { method: "POST", body: req }),
			updateItem: async (id: string, itemId: string, qty: number) =>
				cartCall(id, `/cart/${enc(id)}/items/${enc(itemId)}`, { method: "PATCH", body: { qty } }),
			// Одним запросом, а не N подряд: каждый ответ заменяет корзину целиком,
			// и при последовательном удалении строки исчезали по очереди.
			removeItems: async (id: string, itemIds: string[]) =>
				cartCall(id, `/cart/${enc(id)}/items/delete`, { method: "POST", body: { ids: itemIds } }),
			removeItem: async (id: string, itemId: string) =>
				cartCall(id, `/cart/${enc(id)}/items/${enc(itemId)}`, { method: "DELETE" }),
			applyPromo: async (id: string, code: string) =>
				cartCall(id, `/cart/${enc(id)}/promo`, { method: "POST", body: { code } }),
			clearPromo: async (id: string) => cartCall(id, `/cart/${enc(id)}/promo`, { method: "DELETE" }),
			promoFeed: async (id: string) =>
				CartPromoFeedSchema.parse(
					await request(baseUrl, `/cart/${enc(id)}/promos`, { headers: accessHeaders("cart", id) }),
				),
		};
	})(),
	orders: {
		createPending: async (req: CreateOrderRequest) => {
			const raw = await request(baseUrl, "/orders", {
				method: "POST",
				body: req,
				headers: accessHeaders("cart", req.cartId),
			});
			rememberOrderTokens(raw);
			return Order.parse(raw);
		},
		// Заказ целиком — владельцу или по токену заказа (X-Order-Token); без
		// него шлюз отдаёт платёжный вид без контактов и адреса.
		confirm: async (id: string) => {
			const raw = await request(baseUrl, `/orders/${enc(id)}/confirm`, {
				method: "POST",
				headers: accessHeaders("order", id),
			});
			rememberOrderTokens(raw);
			return Order.parse(raw);
		},
		get: async (id: string) =>
			Order.parse(await request(baseUrl, `/orders/${enc(id)}`, { headers: accessHeaders("order", id) })),
		paySavedCard: async (id: string, paymentMethodId: string) =>
			SavedCardPayResult.parse(
				await request(baseUrl, `/orders/${enc(id)}/pay-saved-card`, {
					method: "POST",
					body: { paymentMethodId },
					headers: accessHeaders("order", id),
				}),
			),
		shipments: async (id: string) =>
			OrderShipment.array().parse(
				await request(baseUrl, `/orders/${enc(id)}/shipments`, { headers: accessHeaders("order", id) }),
			),
	},
	payments: {
		config: async () => PublicPaymentConfig.parse(await request(baseUrl, "/payments/config")),
		// Ссылка на платёжную страницу провайдера (Робокасса). У виджетных
		// провайдеров ручка отвечает 501 — вызывать её там незачем.
		url: async (orderId: string) =>
			PaymentUrl.parse(
				await request(baseUrl, `/orders/${encodeURIComponent(orderId)}/payment-url`, {
					method: "POST",
					headers: accessHeaders("order", orderId),
				}),
			),
	},
	settings: {
		delivery: async () => DeliveryConfig.parse(await request(baseUrl, "/settings/delivery")),
	},
	delivery: {
		quote: async (req: DeliveryQuoteRequest) =>
			DeliveryQuote.parse(await request(baseUrl, "/delivery/quote", { method: "POST", body: req })),
		points: async ({ country, city, postalCode, dressingRoom }) => {
			const q = new URLSearchParams({ city });
			if (country) q.set("country", country);
			if (postalCode) q.set("postalCode", postalCode);
			// Фильтр СДЭК: только ПВЗ с примерочной (для примерки/частичного выкупа).
			if (dressingRoom) q.set("dressingRoom", "1");
			return PickupPoint.array().parse(await request(baseUrl, `/delivery/points?${q.toString()}`));
		},
		selfOptions: async ({ cartId, city, country, lineIds }) => {
			const q = new URLSearchParams({ cartId, city });
			if (country) q.set("country", country);
			if (lineIds?.length) q.set("lineIds", lineIds.join(","));
			return SelfDeliveryShopOption.array().parse(await request(baseUrl, `/delivery/self-options?${q.toString()}`));
		},
		sellerPickupOptions: async ({ cartId, city, country, lineIds }) => {
			const q = new URLSearchParams({ cartId, city });
			if (country) q.set("country", country);
			if (lineIds?.length) q.set("lineIds", lineIds.join(","));
			return SellerPickupShopOption.array().parse(await request(baseUrl, `/delivery/seller-pickup-options?${q.toString()}`));
		},
		productOptions: async ({ productId, city, country, variantId }) => {
			const q = new URLSearchParams({ productId, city });
			if (country) q.set("country", country);
			if (variantId) q.set("variantId", variantId);
			return ProductDeliveryOptions.parse(await request(baseUrl, `/delivery/product-options?${q.toString()}`));
		},
	},
	geo: {
		countries: async () => Country.array().parse(await request(baseUrl, "/geo/countries")),
		currencies: async () => Currency.array().parse(await request(baseUrl, "/geo/currencies")),
		reverse: async (lat: number, lon: number) =>
			GeoReverse.parse(
				await request(baseUrl, `/geo/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}`, {
					auth: false,
				}),
			),
		addressSuggest: async (query: string, city?: string) => {
			const q = new URLSearchParams({ q: query });
			if (city) q.set("city", city);
			return AddressSuggestion.array().parse(
				await request(baseUrl, `/geo/address-suggest?${q.toString()}`),
			);
		},
		citySuggest: async (query: string) => {
			const q = new URLSearchParams({ q: query });
			return CitySuggestion.array().parse(
				await request(baseUrl, `/geo/city-suggest?${q.toString()}`),
			);
		},
	},
	// Телеметрия витрины. Ответ не разбираем: в нём счётчик принятых событий, а
	// решать на его основании витрине нечего. `keepalive` держит запрос живым,
	// когда вкладку закрывают прямо во время отправки пачки.
	discovery: {
		recordEvents: async (input) => {
			await request<void>(baseUrl, "/discovery/events/batch", {
				method: "POST",
				body: input,
				init: { keepalive: true },
			});
		},
		recordOrderEvent: async ({ clientId, ...event }) => {
			await request<void>(baseUrl, "/discovery/events", {
				method: "POST",
				body: { eventType: "order_create", ...(clientId ? { clientId } : {}), ...event },
				init: { keepalive: true },
			});
		},
	},
	feed: {
		// Единая управляемая выдача главной. `parseItems` валидирует блоки
		// поштучно: неизвестный/битый блок пропускаем, а не роняем всю ленту.
		blocks: async (segment, department) => {
			const p = new URLSearchParams({ placement: "home" });
			if (segment) p.set("segment", segment);
			if (department) p.set("department", department);
			const raw = await request<{ blocks?: unknown }>(baseUrl, `/feed/blocks?${p.toString()}`);
			return parseItems(FeedBlockWire, raw?.blocks ?? [], "feed.blocks");
		},
		collections: async () =>
			FeedCollection.array().parse(await request(baseUrl, "/feed/collections")),
		collection: async (slug) =>
			FeedCollection.parse(await request(baseUrl, `/feed/collections/${enc(slug)}`)),
		// Медиа идей лежат на другом хосте (S3 за шлюзом, внешние CDN), и
		// `<a download>` на чужой адрес браузер игнорирует — просто открывает фото.
		// Поэтому файл берём fetch'ем (CORS у медиа открыт) и отдаём blob.
		imageFile: async (url) => {
			const res = await fetch(url, { mode: "cors", credentials: "omit" });
			if (!res.ok) throw new Error(`Image request failed: ${res.status}`);
			return res.blob();
		},
		productCollections: async (productId) =>
			ProductCollectionParticipation.array().parse(
				await request(baseUrl, `/feed/products/${enc(productId)}/collections`),
			),
		forYou: async ({ seed, cursor, clientId, ...query }) => {
			const p = new URLSearchParams(buildSearch(query));
			p.delete("page");
			p.set("seed", seed);
			if (cursor) p.set("cursor", cursor);
			if (clientId) p.set("clientId", clientId);
			return PersonalFeedPage.parse(await request(baseUrl, `/feed/for-you?${p.toString()}`));
		},
	},
	referral: {
		// Переход с каналом (ссылка / QR) и метками - через /referral/landing:
		// шлюз закрепляет код за устройством на 30 дней (PDF «Доработки», п.3).
		visit: async (code, visitor, meta) => {
			await request<unknown>(baseUrl, "/referral/landing", {
				method: "POST",
				body: { promo: code, visitor, channel: meta?.channel, utm: meta?.utm, platform: "web" },
			});
		},
		attribute: async (code) =>
			await request<{ attributed: boolean }>(baseUrl, "/buyer/referral", { method: "POST", body: { promo: code } }),
	},
	shopInvite: {
		resolve: async (code) => {
			try {
				return await request<ShopInvite>(baseUrl, `/shop-invites/${enc(code)}`, { auth: false });
			} catch {
				return null;
			}
		},
		visit: async (code, visitor, meta) => {
			await request<void>(baseUrl, "/shop-invites/visit", {
				method: "POST",
				body: { code, visitor, channel: meta?.channel, utm: meta?.utm, platform: "web" },
			});
		},
		bind: async (code, visitor) =>
			await request<ShopInviteBindResult>(baseUrl, "/buyer/shop-invite", { method: "POST", body: { code, visitor } }),
	},
	accountDeletion: {
		check: async () => AccountDeletionCheck.parse(await request(baseUrl, "/buyer/account/delete/check")),
		start: async () =>
			await request<{ sessionId: string; maskedTarget: string; expiresAt: string }>(baseUrl, "/buyer/account/delete/start", {
				method: "POST",
			}),
		confirm: async (sessionId, code) =>
			AccountDeletionResult.parse(
				await request(baseUrl, "/buyer/account/delete", { method: "POST", body: { sessionId, code, source: "web" } }),
			),
	},
	oauth: {
		providers: async () =>
			OAuthProviderInfo.array().parse(await request(baseUrl, "/buyer/oauth/providers", { auth: false })),
		start: async (provider, input) =>
			await request<{ url: string }>(baseUrl, `/buyer/oauth/${enc(provider)}/start`, {
				method: "POST",
				body: input,
				auth: input.mode === "link",
			}),
		exchange: async (ticket) =>
			OAuthExchangeResult.parse(
				await request(baseUrl, "/buyer/oauth/exchange", { method: "POST", body: { ticket, deviceName: "Browser" }, auth: false }),
			),
		claim: async (ticket) =>
			OAuthIdentity.array().parse(await request(baseUrl, "/buyer/oauth/claim", { method: "POST", body: { ticket } })),
		identities: async () => OAuthIdentity.array().parse(await request(baseUrl, "/buyer/oauth/identities")),
		unlink: async (provider) => {
			await request<void>(baseUrl, `/buyer/oauth/identities/${enc(provider)}`, { method: "DELETE" });
		},
	},
	buyer: {
		startAuth: async (input) =>
			BuyerAuthStartResponse.parse(
				await request(baseUrl, "/buyer/auth/start", { method: "POST", body: input, auth: false }),
			),
		verifyAuth: async (input) =>
			BuyerAuthVerifyResponse.parse(
				await request(baseUrl, "/buyer/auth/verify", { method: "POST", body: input, auth: false }),
			),
		logout: async () => {
			await request<void>(baseUrl, "/buyer/auth/logout", { method: "POST" });
		},
		me: async () => BuyerMe.parse(await request(baseUrl, "/buyer/me")),
		updateProfile: async (input) =>
			BuyerMe.parse(await request(baseUrl, "/buyer/me", { method: "PATCH", body: input })),
		updateProfileExt: async (input) =>
			BuyerMe.parse(await request(baseUrl, "/buyer/profile", { method: "PATCH", body: input })),
		uploadAvatar: async (file) => {
			const form = new FormData();
			form.append("file", file);
			return (await request<{ avatarUrl: string }>(baseUrl, "/buyer/me/avatar", { method: "POST", body: form }));
		},
		deleteAvatar: async () => {
			await request<void>(baseUrl, "/buyer/me/avatar", { method: "DELETE" });
		},
		startPhoneChange: async () =>
			PhoneChangeStart.parse(await request(baseUrl, "/buyer/phone/change/start", { method: "POST" })),
		verifyPhoneChangeCurrent: async (sessionId, code) =>
			PhoneChangeVerify.parse(
				await request(baseUrl, "/buyer/phone/change/verify-current", { method: "POST", body: { sessionId, code } }),
			),
		setPhoneChangeNew: async (sessionId, phone) =>
			PhoneChangeNew.parse(
				await request(baseUrl, "/buyer/phone/change/new", { method: "POST", body: { sessionId, phone } }),
			),
		verifyPhoneChangeNew: async (sessionId, code) =>
			BuyerMe.parse(
				await request(baseUrl, "/buyer/phone/change/verify-new", { method: "POST", body: { sessionId, code } }),
			),
		listSessions: async () => BuyerSession.array().parse(await request(baseUrl, "/buyer/sessions")),
		revokeSession: async (id) => {
			await request<void>(baseUrl, `/buyer/sessions/${enc(id)}`, { method: "DELETE" });
		},
		revokeOtherSessions: async () => {
			await request<void>(baseUrl, "/buyer/sessions/revoke-others", { method: "POST" });
		},
		listAddresses: async () => BuyerAddress.array().parse(await request(baseUrl, "/buyer/addresses")),
		createAddress: async (input: BuyerAddressInput) =>
			BuyerAddress.parse(await request(baseUrl, "/buyer/addresses", { method: "POST", body: input })),
		updateAddress: async (id: string, input: BuyerAddressInput) =>
			BuyerAddress.parse(
				await request(baseUrl, `/buyer/addresses/${enc(id)}`, { method: "PUT", body: input }),
			),
		deleteAddress: async (id: string) => {
			await request<void>(baseUrl, `/buyer/addresses/${enc(id)}`, { method: "DELETE" });
		},
		listRecipients: async () => BuyerRecipient.array().parse(await request(baseUrl, "/buyer/recipients")),
		createRecipient: async (input) =>
			BuyerRecipient.parse(await request(baseUrl, "/buyer/recipients", { method: "POST", body: input })),
		updateRecipient: async (id, input) =>
			BuyerRecipient.parse(await request(baseUrl, `/buyer/recipients/${enc(id)}`, { method: "PUT", body: input })),
		deleteRecipient: async (id) => {
			await request<void>(baseUrl, `/buyer/recipients/${enc(id)}`, { method: "DELETE" });
		},
		listPickupPoints: async () => SavedPickupPoint.array().parse(await request(baseUrl, "/buyer/pickup-points")),
		savePickupPoint: async (input) =>
			SavedPickupPoint.parse(await request(baseUrl, "/buyer/pickup-points", { method: "POST", body: input })),
		deletePickupPoint: async (carrier, code) => {
			await request<void>(baseUrl, `/buyer/pickup-points/${enc(carrier)}/${enc(code)}`, { method: "DELETE" });
		},
		suggestCompany: async (query) =>
			BuyerCompanySuggestion.array().parse(
				await request(baseUrl, `/buyer/company-suggest?query=${encodeURIComponent(query)}`),
			),
		listPaymentMethods: async () =>
			BuyerPaymentMethod.array().parse(await request(baseUrl, "/buyer/payment-methods")),
		createPaymentMethod: async (input: BuyerPaymentMethodInput) =>
			BuyerPaymentMethod.parse(
				await request(baseUrl, "/buyer/payment-methods", { method: "POST", body: input }),
			),
		updatePaymentMethod: async (id: string, patch: BuyerPaymentMethodPatch) =>
			BuyerPaymentMethod.parse(
				await request(baseUrl, `/buyer/payment-methods/${enc(id)}`, { method: "PATCH", body: patch }),
			),
		deletePaymentMethod: async (id: string) => {
			await request<void>(baseUrl, `/buyer/payment-methods/${enc(id)}`, { method: "DELETE" });
		},
		listSbpBanks: async () => SbpBank.array().parse(await request(baseUrl, "/payments/sbp/banks", { auth: false })),
		createSbpPaymentMethod: async (input) =>
			BuyerPaymentMethod.parse(
				await request(baseUrl, "/buyer/payment-methods/sbp", { method: "POST", body: input }),
			),
		createCompanyPaymentMethod: async (input) =>
			BuyerPaymentMethod.parse(
				await request(baseUrl, "/buyer/payment-methods/company", { method: "POST", body: input }),
			),
		getBalance: async () => BuyerBalance.parse(await request(baseUrl, "/buyer/balance")),
		listLedger: async () =>
			LedgerEntry.array().parse(await request(baseUrl, "/buyer/balance/ledger")),
		topupBalance: async (amount: number) =>
			BalanceTopup.parse(
				await request(baseUrl, "/buyer/balance/topup", { method: "POST", body: { amount } }),
			),
		// Робокасса: ссылка на оплату пополнения (тот же редирект, что у заказа).
		topupPaymentUrl: async (id: string) =>
			PaymentUrl.parse(
				await request(baseUrl, `/buyer/balance/topup/${enc(id)}/payment-url`, { method: "POST" }),
			),
		confirmTopup: async (id: string) =>
			TopupConfirmResult.parse(
				await request(baseUrl, `/buyer/balance/topup/${enc(id)}/confirm`, { method: "POST" }),
			),
		getBillingProfile: async () => {
			const raw = await request(baseUrl, "/buyer/billing-profile");
			return raw == null ? null : BillingProfile.parse(raw);
		},
		updateBillingProfile: async (input: BillingProfileInput) =>
			BillingProfile.parse(
				await request(baseUrl, "/buyer/billing-profile", { method: "PUT", body: input }),
			),
		getOrderInvoice: async (id: string) =>
			Invoice.parse(await request(baseUrl, `/buyer/orders/${enc(id)}/invoice`)),
		listOrders: async () => parseItems(Order, await request(baseUrl, "/buyer/orders"), "buyer.orders"),
		getOrder: async (id) => Order.parse(await request(baseUrl, `/buyer/orders/${enc(id)}`)),
		cancelOrder: async (id, reason) =>
			Order.parse(
				await request(baseUrl, `/buyer/orders/${enc(id)}/cancel`, {
					method: "POST",
					body: { reason },
				}),
			),
		repeatOrder: async (id) =>
			Cart.parse(await request(baseUrl, `/buyer/orders/${enc(id)}/repeat`, { method: "POST" })),
		reviewOrder: async (id, input) =>
			Order.parse(
				await request(baseUrl, `/buyer/orders/${enc(id)}/review`, { method: "POST", body: input }),
			),
		requestOrderReturn: async (id, input) =>
			Order.parse(
				await request(baseUrl, `/buyer/orders/${enc(id)}/return-request`, {
					method: "POST",
					body: input,
				}),
			),
		completeOrder: async (id) =>
			Order.parse(await request(baseUrl, `/buyer/orders/${enc(id)}/complete`, { method: "POST" })),
		listMessages: async () => BuyerMessage.array().parse(await request(baseUrl, "/buyer/messages")),
		listConversations: async () =>
			ChatConversation.array().parse(await request(baseUrl, "/buyer/conversations")),
		getConversation: async (id) =>
			ChatConversation.parse(await request(baseUrl, `/buyer/conversations/${enc(id)}`)),
		openSupportConversation: async (message) =>
			ChatConversation.parse(
				await request(baseUrl, "/buyer/conversations/support", {
					method: "POST",
					body: message ? { message } : {},
				}),
			),
		startProductConversation: async (input) =>
			ChatConversation.parse(await request(baseUrl, "/buyer/conversations", { method: "POST", body: input })),
		startShopConversation: async (input) =>
			ChatConversation.parse(await request(baseUrl, "/buyer/conversations/shop", { method: "POST", body: input })),
		listCustomOrders: async (shopId) =>
			ShopCustomOrderListSchema.parse(
				await request(baseUrl, `/buyer/custom-orders${shopId ? `?shopId=${enc(shopId)}` : ""}`),
			),
		cancelCustomOrder: async (id) =>
			ShopCustomOrderSchema.parse(await request(baseUrl, `/buyer/custom-orders/${enc(id)}/cancel`, { method: "POST" })),
		replyConversation: async (id, body, imageUris, brief) =>
			ChatMessage.parse(
				await request(baseUrl, `/buyer/conversations/${enc(id)}/messages`, {
					method: "POST",
					body: {
						body,
						...(imageUris?.length ? { imageUris } : {}),
						...(brief?.productId ? { productId: brief.productId } : {}),
					},
				}),
			),
		markConversationRead: async (id) => {
			await request(baseUrl, `/buyer/conversations/${enc(id)}/read`, { method: "POST" });
		},
		pinConversations: async (ids, pinned) =>
			ChatBulkResult.parse(await request(baseUrl, "/buyer/conversations/pin", { method: "POST", body: { ids, pinned } })),
		deleteConversations: async (ids) =>
			ChatBulkResult.parse(await request(baseUrl, "/buyer/conversations/delete", { method: "POST", body: { ids } })),
		uploadChatImage: async (file) => {
			const form = new FormData();
			form.append("file", file);
			return ChatUpload.parse(await request(baseUrl, "/buyer/conversations/attachments", { method: "POST", body: form }));
		},
		acceptOffer: async (offerId, cartId) =>
			Cart.parse(
				await request(baseUrl, `/buyer/offers/${enc(offerId)}/accept`, {
					method: "POST",
					body: { cartId },
				}),
			),
		listRequests: async () =>
			BuyerRequestListSchema.parse(await request(baseUrl, "/buyer/requests")),
		getRequest: async (id) =>
			BuyerRequestDetailSchema.parse(await request(baseUrl, `/buyer/requests/${enc(id)}`)),
		createRequest: async (input) =>
			BuyerRequestDetailSchema.parse(
				await request(baseUrl, "/buyer/requests", { method: "POST", body: input }),
			),
		updateRequest: async (id, input) =>
			BuyerRequestDetailSchema.parse(
				await request(baseUrl, `/buyer/requests/${enc(id)}`, { method: "PATCH", body: input }),
			),
		cancelRequest: async (id) =>
			BuyerRequestDetailSchema.parse(
				await request(baseUrl, `/buyer/requests/${enc(id)}/cancel`, { method: "POST" }),
			),
		addRequestAttachment: async (id, file) => {
			const form = new FormData();
			form.append("file", file);
			return BuyerRequestAttachmentSchema.parse(
				await request(baseUrl, `/buyer/requests/${enc(id)}/attachments`, {
					method: "POST",
					body: form,
				}),
			);
		},
		declineOffer: async (offerId) =>
			RequestOfferSchema.parse(
				await request(baseUrl, `/buyer/offers/${enc(offerId)}/decline`, { method: "POST" }),
			),
		acceptOfferInChat: async (offerId) =>
			RequestOfferSchema.parse(
				await request(baseUrl, `/buyer/offers/${enc(offerId)}/accept-in-chat`, { method: "POST" }),
			),
		listProductLikes: async (input) =>
			PaginatedLikeIds.parse(await request(baseUrl, `/buyer/likes/products?${pageQuery(input)}`)),
		listShopLikes: async (input) =>
			PaginatedLikeIds.parse(await request(baseUrl, `/buyer/likes/shops?${pageQuery(input)}`)),
		listIdeaLikes: async (input) =>
			PaginatedLikeIds.parse(await request(baseUrl, `/buyer/likes/ideas?${pageQuery(input)}`)),
		setProductLiked: async (productId, liked, size) =>
			LikeTargetResult.parse(
				await request(baseUrl, `/buyer/likes/products/${enc(productId)}`, {
					method: "PUT",
					body: { liked, size },
				}),
			),
		setShopLiked: async (shopId, liked) =>
			LikeTargetResult.parse(
				await request(baseUrl, `/buyer/likes/shops/${enc(shopId)}`, {
					method: "PUT",
					body: { liked },
				}),
			),
		setReviewLiked: async (reviewId, liked) =>
			LikeTargetResult.parse(
				await request(baseUrl, `/buyer/likes/reviews/${enc(reviewId)}`, {
					method: "PUT",
					body: { liked },
				}),
			),
		askQuestion: async (productId, body) =>
			ProductQuestionSchema.parse(
				await request(baseUrl, `/buyer/products/${enc(productId)}/questions`, {
					method: "POST",
					body: { body },
				}),
			),
		setIdeaLiked: async (ideaId, liked) =>
			LikeTargetResult.parse(
				await request(baseUrl, `/buyer/likes/ideas/${enc(ideaId)}`, {
					method: "PUT",
					body: { liked },
				}),
			),
		listHiddenIdeas: async () => HiddenIdeaState.parse(await request(baseUrl, "/buyer/ideas/hidden")),
		setIdeaHidden: async (ideaId, hidden) =>
			HiddenTargetResult.parse(
				await request(baseUrl, `/buyer/ideas/hidden/${enc(ideaId)}`, {
					method: "PUT",
					body: { hidden },
				}),
			),
		importGuestLikes: async (clientId) =>
			LikeState.parse(
				await request(baseUrl, "/buyer/likes/import", { method: "POST", body: { clientId } }),
			),
		attachDevice: async (device) =>
			DeviceAttachResult.parse(
				await request(baseUrl, "/buyer/device/attach", { method: "POST", body: device }),
			),
	},
	notifications: {
		list: async (query) => {
			const params = new URLSearchParams({
				limit: String(query.limit ?? 20),
				filter: query.filter ?? "all",
				locale: query.locale ?? "ru",
			});
			if (query.cursor) params.set("cursor", query.cursor);
			// fd-notif-17: фильтр ленты по категории.
			if (query.category) params.set("category", query.category);
			// Веб-макет «Профиль»: фильтр по темам покупателя (через запятую).
			if (query.topics) params.set("topics", query.topics);
			return NotificationPageSchema.parse(await request(baseUrl, `/notifications?${params.toString()}`));
		},
		unreadCount: async () =>
			NotificationUnreadCountSchema.parse(await request(baseUrl, "/notifications/unread-count")),
		detail: async (id, locale) =>
			NotificationItemSchema.parse(
				await request(baseUrl, `/notifications/${enc(id)}?locale=${enc(locale)}`),
			),
		markRead: async (id) => {
			await request(baseUrl, `/notifications/${enc(id)}/read`, { method: "POST" });
		},
		markAllRead: async () => {
			await request(baseUrl, "/notifications/read-all", { method: "POST" });
		},
		markReadBySource: async (body) => {
			await request(baseUrl, "/notifications/read-by-source", { method: "POST", body });
		},
		getPreferences: async () =>
			NotificationPreferencesSchema.parse(await request(baseUrl, "/notifications/preferences")),
		getTopicSettings: async () =>
			NotificationTopicSettingsSchema.parse(await request(baseUrl, "/notifications/topic-settings")),
		updateTopicSettings: async (patch) =>
			NotificationTopicSettingsSchema.parse(
				await request(baseUrl, "/notifications/topic-settings", { method: "PUT", body: patch }),
			),
		topicCounts: async (filter = "all") =>
			NotificationTopicCountsSchema.parse(
				await request(baseUrl, `/notifications/topic-counts${filter === "all" ? "" : `?filter=${enc(filter)}`}`),
			),
		archive: async (id) => {
			await request(baseUrl, `/notifications/${enc(id)}/archive`, { method: "POST" });
		},
		archiveAll: async (topics) => {
			const q = topics && topics.length > 0 ? `?topics=${enc(topics.join(","))}` : "";
			await request(baseUrl, `/notifications/archive-all${q}`, { method: "POST" });
		},
		updatePreferences: async (patch) =>
			NotificationPreferencesSchema.parse(
				await request(baseUrl, "/notifications/preferences", { method: "PUT", body: patch }),
			),
		telegramStatus: async () =>
			TelegramLinkStatusSchema.parse(await request(baseUrl, "/notifications/channels/telegram")),
		telegramLink: async () =>
			TelegramLinkStartSchema.parse(
				await request(baseUrl, "/notifications/channels/telegram/link", { method: "POST", body: { app: "buyer" } }),
			),
		telegramUnlink: async () => {
			await request(baseUrl, "/notifications/channels/telegram", { method: "DELETE" });
		},
	},
	support: {
		faq: async (category) =>
			FaqListSchema.parse(
				// Витрине — статьи для покупателей и общие (консоль, 26.09.2026).
				await request(baseUrl, `/support/faq?audience=buyers${category ? `&category=${enc(category)}` : ""}`, {
					auth: false,
				}),
			),
		listTickets: async () => {
			const payload = await request<{ items?: unknown }>(baseUrl, "/support/tickets");
			return SupportTicketSchema.array().parse(payload?.items ?? []);
		},
		getTicket: async (id) =>
			SupportTicketDetailSchema.parse(await request(baseUrl, `/support/tickets/${enc(id)}`)),
		createTicket: async (input) =>
			SupportTicketDetailSchema.parse(
				await request(baseUrl, "/support/tickets", { method: "POST", body: input }),
			),
		addMessage: async (id, body) =>
			SupportTicketDetailSchema.parse(
				await request(baseUrl, `/support/tickets/${enc(id)}/messages`, {
					method: "POST",
					body: { body },
				}),
			),
		closeTicket: async (id) =>
			SupportTicketDetailSchema.parse(
				await request(baseUrl, `/support/tickets/${enc(id)}/close`, { method: "POST" }),
			),
	},
	public: {
		metrika: async () =>
			MetrikaConfigSchema.parse(await request(baseUrl, "/public/metrika", { auth: false })),
	},
	engagement: {
		getLikes: async (clientId) =>
			LikeState.parse(await request(baseUrl, `/likes?clientId=${enc(clientId)}`, { auth: false })),
		setProductLiked: async (clientId, productId, liked, size) =>
			LikeTargetResult.parse(
				await request(baseUrl, `/likes/products/${enc(productId)}`, {
					method: "PUT",
					body: { clientId, liked, size },
					auth: false,
				}),
			),
		setShopLiked: async (clientId, shopId, liked) =>
			LikeTargetResult.parse(
				await request(baseUrl, `/likes/shops/${enc(shopId)}`, {
					method: "PUT",
					body: { clientId, liked },
					auth: false,
				}),
			),
		setIdeaLiked: async (clientId, ideaId, liked) =>
			LikeTargetResult.parse(
				await request(baseUrl, `/likes/ideas/${enc(ideaId)}`, {
					method: "PUT",
					body: { clientId, liked },
					auth: false,
				}),
			),
		getHiddenIdeas: async (clientId) =>
			HiddenIdeaState.parse(await request(baseUrl, `/ideas/hidden?clientId=${enc(clientId)}`, { auth: false })),
		setIdeaHidden: async (clientId, ideaId, hidden) =>
			HiddenTargetResult.parse(
				await request(baseUrl, `/ideas/hidden/${enc(ideaId)}`, {
					method: "PUT",
					body: { clientId, hidden },
					auth: false,
				}),
			),
		sendProductMessage: async ({ productId, ...body }) =>
			ProductMessage.parse(
				await request(baseUrl, `/messages/products/${enc(productId)}`, {
					method: "POST",
					body,
				}),
			),
		createProductManufacturingRequest: async ({ productId, ...body }) =>
			ProductManufacturingRequest.parse(
				await request(baseUrl, `/requests/products/${enc(productId)}/manufacturing`, {
					method: "POST",
					body,
				}),
			),
	}, 
	seo: {
		page: async (path) =>
			SeoPageSchema.parse(await request(baseUrl, `/seo/page?path=${enc(path)}`, { auth: false })),
	},
	push: {
		config: async () => WebPushConfigSchema.parse(await request(baseUrl, "/push/config", { auth: false })),
		subscribe: async (input) => {
			await request(baseUrl, "/buyer/push-subscriptions", { method: "POST", body: input });
		},
		unsubscribe: async (endpoint) => {
			await request(baseUrl, "/buyer/push-subscriptions", { method: "DELETE", body: { endpoint } });
		},
	},
});
